import { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import Paginator from '../../../components/Paginator';
import ModalComponent from '../../../components/ModalComponent';
import { VideoTypeValue } from '../../../utils/video';
import FileItem from '../../My/Files/FileItem';
import { VideoContext, VideoInterface } from '../../../contexts/VideoContext';
import styles from './BlockVideosManagement.module.css';

type modalPropertiesType = 'delete' | 'create' | 'edit' | 'video_preview' | null;

interface ModalBodyProps {
  modalProperties: {
    isOpen: boolean,
    title: string,
    type: modalPropertiesType,
    nofooter?: boolean,
  }
  file: VideoInterface
  inputFile: File | null
  setInputFile: (file: File) => void
  errors: any
}

const ModalBody = ({
  modalProperties,
  file,
  inputFile,
  setInputFile,
  errors,
}: ModalBodyProps) => {
  let fileName = '';
  if (inputFile) {
    fileName = inputFile.name;
  } else if (modalProperties.type === 'create') {
    fileName = '';
  } else {
    fileName = file ? file.file_name : '';
  }

  if (modalProperties.type === 'delete') {
    return (
      <span>
        {`Delete "${fileName}"?`}
      </span>
    );
  }

  if (modalProperties.type === 'video_preview') {
    return (
      <div>
        <video
          width="100%"
          height="auto"
          controls
          src={file.src || ''}
        >
          <track kind="captions" />
        </video>
      </div>
    );
  }

  return (
    <>
      {errors && errors?.videoFile && (
        <div className="p-1 alert alert-danger" role="alert">
          {errors.videoFile}
        </div>
      )}

      <form className="d-flex flex-column h-100">
        <div className="row">
          <div className="col-12">
            <div className="row g-3 mb-3">
              <label className="col-form-label col-sm-2 text-sm-start">
                File
                <span>*</span>
              </label>
              <div className="col-sm-10">
                <div className="d-flex justify-content-center align-items-center">
                  <input
                    type="text"
                    className="form-control me-2"
                    readOnly
                    placeholder="Please select a file"
                    id="fileselect"
                    value={fileName}
                  />
                  <label className="input-group-btn">
                    <span className="btn btn-primary text-nowrap">
                      Choose File
                      <input
                        type="file"
                        accept="video/mp4,video/webm"
                        style={{ display: 'none' }}
                        ref={(input) => {
                          if (input) {
                            input.addEventListener('change', () => {
                              if (input.files) {
                                setInputFile(input.files[0]);
                                document.getElementById('fileselect')?.setAttribute('value', input.files[0].name);
                              }
                            });
                          }
                        }}
                      />
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

function BlockVideosManagement(): React.ReactElement {
  const {
    videos,
    total,
    currentPage,
    errors,
    links,
    isLoading,
    fetchVideos,
    createVideo,
    editVideo,
    deleteVideo,
    fromItem,
    toItem,
  } = useContext(VideoContext);

  const [inputFile, setInputFile] = useState<File | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [modalProperties, setModalProperties] = useState<{
    isOpen: boolean,
    title: string,
    type: modalPropertiesType,
    nofooter?: boolean,
  }>({
    isOpen: false,
    type: null,
    title: '',
    nofooter: false,
  });

  const handleModalClose = () => {
    setModalProperties({ ...modalProperties, isOpen: false });
    setInputFile(null);
    setSelectedIndex(null);
  };

  const handleConfirm = () => {
    if (modalProperties.type === 'delete') {
      if (selectedIndex === null) return;

      console.log('delete video', videos[selectedIndex].id);

      deleteVideo(
        videos[selectedIndex].id,
        VideoTypeValue.MY_VIDEO,
        handleModalClose,
      );
    } else if (modalProperties.type === 'edit') {
      // if file is not selected, do nothing
      if (inputFile === null || selectedIndex === null) {
        handleModalClose();
        return;
      }

      editVideo(
        videos[selectedIndex].id,
        VideoTypeValue.MY_VIDEO,
        inputFile,
        handleModalClose,
      );
    } else {
      createVideo(
        inputFile as File,
        VideoTypeValue.MY_VIDEO,
        handleModalClose,
        () => {
          setModalProperties({ ...modalProperties, isOpen: true });
        },
      );
    }
  };

  useEffect(() => {
    fetchVideos(VideoTypeValue.MY_VIDEO, currentPage);
  }, [currentPage, fetchVideos]);

  useEffect(() => {
    setSelectedIndex(selectedIndex);
  }, [selectedIndex]);

  return (
    <>
      <main className={styles.main}>
        <div className="container-fluid p-3">
          <div className="mb-3">
            <h3 className="h3 d-inline align-middle">Videos</h3>
          </div>

          <div className="card">
          <div className="card-header border-0">
            <h4 className="h4 float-start mt-2">My Videos</h4>
            <div className="float-end mr-1">
              <Button
                className="btn btn-primary"
                variant="contained"
                component="label"
                disabled={isLoading}
                sx={{
                  position: 'relative',
                }}
              >
                {
                  isLoading
                    ? (<CircularProgress size={20} />)
                    : (
                      <>
                        <span onClick={() => setModalProperties({ isOpen: true, type: 'create', title: 'Upload Video' })}>
                          Upload Video
                        </span>
                      </>
                    )
                }
              </Button>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive border-bottom">
              <table className="table table-striped table-hover table-bordered table-nowrap">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: '5%;' }}>Number</th>
                    <th>File Name</th>
                    <th className="text-center" style={{ width: '100px;' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {videos?.map((video, index) => (
                    <FileItem
                      index={index + 1}
                      fileName={video.file_name}
                      setSelectedIndex={setSelectedIndex}
                      openEditModal={() => setModalProperties({ isOpen: true, type: 'edit', title: 'Replace Video' })}
                      openDeleteModal={() => setModalProperties({ isOpen: true, type: 'delete', title: 'Confirm' })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer bg-white pt-1 pb-3">
            <div className="d-flex mt-2">
              <div className="page-item-count">
                <div style={{ fontSize: 14 }}>
                  {`${fromItem}-${toItem} of ${total}`}
                </div>
              </div>
              <Paginator
                currentPage={currentPage}
                changePage={(page) => fetchVideos(VideoTypeValue.MY_VIDEO, page)}
                links={links}
              />
            </div>
          </div>
        </div>
        </div>
      </main>

      <ModalComponent
        style={{ zIndex: 1050 }}
        show={modalProperties.isOpen}
        centered
        size={modalProperties.type === 'delete' ? undefined : 'lg'}
        onConfirm={handleConfirm}
        closeModal={handleModalClose}
        title={modalProperties.title}
        confirmButtonColor={modalProperties.type === 'delete' ? 'danger' : 'primary'}
        confirmButtonText={modalProperties.type === 'delete' ? 'Delete' : 'Add'}
        nofooter={modalProperties.nofooter}
        cancelButtonColor="outline-secondary"
        cancelButtonText={modalProperties.type === 'delete' ? 'Cancel' : 'Close'}
        loading={isLoading}
      >
        <ModalBody
          file={videos?.[selectedIndex ?? 0] ?? {} as VideoInterface}
          inputFile={inputFile}
          setInputFile={setInputFile}
          modalProperties={modalProperties}
          errors={errors}
        />
        <div>
          <label className="opacity-75">*Supported: mp4, webm</label>
          <br />
          <label className="opacity-75">*Max size: 10MB</label>
        </div>
      </ModalComponent>
    </>
  );
}

export default BlockVideosManagement;
